/** Cookie key：广告来源（Proxy 门禁 + 客户端分流共用） */
export const AD_SOURCE_COOKIE_KEY = "edgen_ad_source";

/**
 * 会话 cookie（不设过期，关浏览器即失效）：本次入口（URL 参数 / 原生深链）带了 ad_source。
 *
 * 为什么要它（EDG-543）：后端 track_meta.ad_source 只在登录 / 签名 / 支付校验时更新，已登录用户
 * 新点广告后后端仍是旧值；EntryGate 每次都用后端值覆盖 cookie，会冲掉本次点击的来源 ——
 * Ask Ed 广告问题与 adchat_* 埋点都读它。命中本标记时跳过回填，其余情况照常「后端优先」。
 * 不用「本地值是否为空」判定：cookie 一年有效，直接打开时本地是上次的值。原生同义实现见
 * `Attribution.reconcileAdSourceFromBackend`（kmp-app）。
 */
export const AD_SOURCE_FROM_ENTRY_COOKIE_KEY = "edgen_ad_source_from_entry";

/**
 * 命中即跳过 onboarding 直落 Brief 的广告来源集合（统一大写；SAVING_ONBOARDING_SKIP 同 SAVING
 * 承接但额外跳过 onboarding；ROAST_HANDOFF 同 ALPHA_PICK/PORTFOLIO_CHECK 直落 Home）
 *
 * ⚠️ **`AI_JOB_RISK` 不得加进来** —— 它是「走另一套 onboarding」，不是「跳过 onboarding」。
 * 该来源走 9 题问诊 + AI 冲击结果页，归 `adSourceToScene()` 分流，两件事不要混。
 * 回归断言钉在 `__tests__/adSourceToScene.test.ts`。
 */
export const AD_SKIP_SOURCES = new Set([
  "ALPHA_PICK",
  "PORTFOLIO_CHECK",
  "SAVING_ONBOARDING_SKIP",
  "ROAST_HANDOFF",
]);

/**
 * Chat 广告承接来源（EDG-543）。
 *
 * ⚠️ **不得加入 {@link AD_SKIP_SOURCES}** —— 那个集合语义是「跳过 onboarding 直落 Home/Brief」，
 * 本来源要落 **Ask Ed 首屏**，是第三个落点（`OnboardingEntry.CHAT`）。与上面 `AI_JOB_RISK`
 * 不得加入该集合是同一类区分：三者分别是「跳过」「换一套」「另一个落点」，两两不同。
 * 回归断言钉在 `__tests__/adChatSource.test.ts`。
 */
export const AD_SOURCE_CHAT_PROMPT = "CHAT_PROMPT";

/**
 * 判定是否为 Chat 广告承接会话（纯函数；统一大写比较，与 `setAdSourceCookie` 的归一一致）。
 */
export function isAdChatHandoff(value: string | null | undefined): boolean {
  const trimmed = value?.trim();
  return !!trimmed && trimmed.toUpperCase() === AD_SOURCE_CHAT_PROMPT;
}

/** 判定某 ad_source 值是否应跳过 onboarding（纯函数，Proxy/客户端共用；统一大写比较） */
export function isAdSkipSource(value: string | null | undefined): boolean {
  if (!value) {
    return false;
  }
  return AD_SKIP_SOURCES.has(value.toUpperCase());
}

/**
 * URL 查询参数名 —— 投放落地到 **web** 时的 ad_source 入口。
 *
 * 为什么需要它：Adjust 链接（`edgen.go.link` / `edwealth.go.link` / `wbsy.adj.st`）在用户
 * **没装 App** 时会 fallback 到 web。此前 web 侧只有两个 cookie 写入点 —— 原生 bridge 深链
 * （`lib/bridge/handleDeepLinkOpened.ts`）与登录后的后端回填（`useOnboardingEntryGate`）——
 * 二者都要求用户已经在 App 里。于是 fallback 到 web 的那批人 `ad_source` 直接丢失，
 * 被当自然流量走默认 onboarding，广告承接在 web 上整条失效。
 */
export const AD_SOURCE_QUERY_KEY = "ad_source";

/**
 * Chat 广告承接（EDG-543）的 `landing_id` 在 web 上的入口：URL 参数 → 短期 cookie → 内存 store。
 *
 * 为什么要 cookie：Proxy 捕获 `ad_source` 后会 307 去掉**全部**参数，未登录用户还要再跳
 * `/login`，`landing_id` 在这两跳里必丢；而它只活在内存 store（{@link import("./adSourceStore")}）。
 * 只对 `CHAT_PROMPT` 落 cookie —— 主题榜单等其它承接的 landing_id 有「曝光即计消」语义，不在此扩散。
 */
export const AD_LANDING_ID_QUERY_KEY = "landing_id";
export const AD_CHAT_LANDING_ID_COOKIE_KEY = "edgen_ad_chat_landing_id";

/**
 * 落点意图（EDG-543）：与「归因」分离的一次性落点信号。
 *
 * 为什么不直接读 `ad_source`：它是**归因**，cookie 一年有效，且登录后被后端 track_meta 回填覆盖
 * （后端优先）。落点若读它，非默认落点（Ask Ed）会在广告用户此后每次打开页面时重复触发。
 * 落点意图只从**本次**入口捕获（URL 参数经 Proxy 落一次性 cookie / 原生深链写内存），
 * 不接受后端回填，落地后即清 —— 二次打开天然回默认流程。
 *
 * 新增「非默认落点」的广告来源：只在 {@link ENTRY_INTENT_BY_SOURCE} 加一行；不在表里的来源
 * 不产生意图，行为逐字不变。存量来源（ALPHA_PICK 等落 Brief）本期未迁移，仍走 `AD_SKIP_SOURCES`。
 * 原生同义实现见 `Attribution.ENTRY_INTENT_BY_SOURCE`（kmp-app），两端必须一致。
 */
export type EntryIntent = "chat";

export const ENTRY_INTENT_BY_SOURCE: Readonly<Record<string, EntryIntent>> = {
  [AD_SOURCE_CHAT_PROMPT]: "chat",
};

/** Proxy 落的一次性意图 cookie（落地后客户端删除；1 天兜底过期）。 */
export const AD_ENTRY_INTENT_COOKIE_KEY = "edgen_entry_intent";

export function resolveEntryIntent(
  adSource: string | null | undefined,
): EntryIntent | null {
  return ENTRY_INTENT_BY_SOURCE[adSource?.trim().toUpperCase() ?? ""] ?? null;
}

export function isEntryIntent(value: unknown): value is EntryIntent {
  return (
    typeof value === "string" &&
    Object.values(ENTRY_INTENT_BY_SOURCE).includes(value as EntryIntent)
  );
}

/** landing_id 允许的字符集；**大小写保真**（后端按主键精确匹配）。 */
const LANDING_ID_PATTERN = /^[A-Za-z0-9_.-]{1,128}$/;

/** 校验来自 URL 的 landing_id；非法返回 null。不改大小写。 */
export function sanitizeLandingIdParam(
  raw: string | null | undefined,
): string | null {
  const trimmed = raw?.trim();
  if (!trimmed || !LANDING_ID_PATTERN.test(trimmed)) {
    return null;
  }
  return trimmed;
}

/** `ad_source` 允许的字符集 —— 该值会写进 cookie 并作为 `OS-Ad-Source` 头发给后端。 */
const AD_SOURCE_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;

/**
 * 校验并规范化来自 URL 的 ad_source；非法返回 null。
 *
 * **不做枚举白名单**：ad_source 的全集由投放侧定义、随时新增，前端硬编码一份必然过期
 * （`AD_SKIP_SOURCES` 只是「跳过 onboarding」那个子集，不是全集）。这里只保证它是个
 * 安全的标识符 —— 未知值经 `adSourceToScene()` 自然落回 `"default"`。
 *
 * 大写化与 {@link import("./adSourceCookie.client").setAdSourceCookie} 保持一致，
 * 否则同一来源会因大小写在 cookie 里存成两种形态。
 */
export function sanitizeAdSourceParam(
  raw: string | null | undefined,
): string | null {
  const trimmed = raw?.trim();
  if (!trimmed || !AD_SOURCE_PATTERN.test(trimmed)) {
    return null;
  }
  return trimmed.toUpperCase();
}

/** 从原生深链 payload 提取 ad_source（businessQuery.ad_source）。 */
export function extractAdSourceFromDeepLink(
  data: { businessQuery?: Record<string, string> } | null | undefined,
): string | null {
  const raw = data?.businessQuery?.ad_source?.trim();
  return raw ? raw : null;
}

/** 从原生深链 payload 提取 query_source（businessQuery.query_source，兴趣分类广告来源）。 */
export function extractQuerySourceFromDeepLink(
  data: { businessQuery?: Record<string, string> } | null | undefined,
): string | null {
  const raw = data?.businessQuery?.query_source?.trim();
  return raw ? raw : null;
}

/**
 * 从原生深链 payload 提取 landing_id（businessQuery.landing_id，主题榜单广告承接素材主键）。
 * 仅 trim()，大小写保真 —— 后端按主键精确匹配并自行兼容大小写，不沿用 ad_source 的大写化。
 */
export function extractLandingIdFromDeepLink(
  data: { businessQuery?: Record<string, string> } | null | undefined,
): string | null {
  const raw = data?.businessQuery?.landing_id?.trim();
  return raw ? raw : null;
}
